import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Avatar, Badge, Button, Mark, cx, useFocusTrap } from '@salonomy/ui';
import { useAppointments } from '../data/appointments.js';
import { useQueue, useQueueLiveSubscription } from '../data/queue.js';
import { useSalon } from '../data/salon.js';
import { useAuth, useMemberships } from '../lib/auth.js';
import { useOffline } from '../lib/offline/provider.js';
import { initials, moneyWhole, pluralize } from '../lib/format.js';
import { todayInZone } from '../lib/time.js';
import { CommandPalette } from '../features/search/index.js';
import { HelpDrawer } from '../features/help/index.js';
import { NotificationsBell, NotificationsDrawer } from '../features/notifications/index.js';

interface NavEntry {
  to: string;
  label: string;
  badge?: boolean;
  managerOnly?: boolean;
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Diary' },
  { to: '/my-week', label: 'My week' },
  { to: '/my-book', label: 'My book' },
  { to: '/queue', label: 'Queue', badge: true },
  { to: '/clients', label: 'Clients' },
  { to: '/reviews', label: 'Reviews', managerOnly: false },
  { to: '/takings', label: 'Takings' },
  { to: '/payments', label: 'Payments', managerOnly: true },
  { to: '/comms', label: 'Comms', managerOnly: true },
  { to: '/reports', label: 'Reports', managerOnly: true },
  { to: '/stock', label: 'Stock', managerOnly: true },
  { to: '/packages', label: 'Packages', managerOnly: true },
  { to: '/settings', label: 'Settings', managerOnly: true },
];

function SyncChip() {
  const { online, pending } = useOffline();
  if (!online) {
    return (
      <span className="sync-chip sync-chip--offline" title="Working offline — changes will sync">
        <span className="sync-chip__dot" /> Offline
      </span>
    );
  }
  if (pending > 0) {
    return (
      <span className="sync-chip sync-chip--pending" title="Syncing queued changes">
        <span className="sync-chip__dot" /> Syncing {pending}
      </span>
    );
  }
  return null;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, isManager, logout, switchSalon } = useAuth();
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);
  const { data: appts } = useAppointments(today, salon?.timezone);
  const { data: queue } = useQueue();
  const { data: memberships } = useMemberships();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // WS-14 — global search palette + in-app help (refs restore focus to the opener on close).
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // WS-14 — notifications inbox (the bell opens a right-slide modal drawer; same open/close idiom).
  const [notifOpen, setNotifOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const searchBtnRef = useRef<HTMLButtonElement>(null);
  const helpBtnRef = useRef<HTMLButtonElement>(null);
  const notifBtnRef = useRef<HTMLButtonElement>(null);
  const notifTopbarBtnRef = useRef<HTMLButtonElement>(null);

  // The whole authed session shares one queue SSE connection (ADR-0004).
  useQueueLiveSubscription(true);

  // WS-14 — Cmd/Ctrl-K toggles the global search palette from anywhere in the console (the shortcut is
  // the whole point, so it fires even from a text field). The palette's own focus trap takes over once
  // mounted; on close, the search button (the opener) regains focus via the trap's restore.
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const openNotif = useCallback(() => setNotifOpen(true), []);
  const closeNotif = useCallback(() => setNotifOpen(false), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Close drawer on Escape and trap/restore focus — the single shared @salonomy/ui trap.
  // On close, focus returns to the hamburger (the element that opened the drawer).
  useFocusTrap(drawerRef, drawerOpen, closeDrawer, { restoreFocus: hamburgerRef });

  // Close the drawer and the WS-14 overlays whenever the route changes (nav item tapped, or a search
  // result navigated). The palette/help close themselves on navigate too; this is the belt-and-braces.
  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
    setHelpOpen(false);
    setNotifOpen(false);
  }, [pathname]);

  const takings = useMemo(() => {
    const done = (appts ?? []).filter((a) => a.status === 'completed');
    const total = done.reduce((sum, a) => sum + a.priceQuoted, 0);
    const fromQueue = (appts ?? []).filter(
      (a) => a.queueTicketId != null || a.source === 'queue' || a.source === 'walk_in',
    ).length;
    const clients = new Set((appts ?? []).map((a) => a.clientDisplayName)).size;
    return { total, clients, fromQueue };
  }, [appts]);

  const waiting = queue?.waitingCount ?? 0;
  const nav = NAV.filter((n) => !n.managerOnly || isManager);

  return (
    <div className="app-shell">
      {/* ── Desktop / wide-tablet rail (≥769px) ── */}
      <aside className="app-rail">
        <Link to="/" aria-label="Salonomy — home" className="app-logo">
          <Mark src={`${import.meta.env.BASE_URL}salonomy-logo.svg`} height={34} />
        </Link>

        {/* WS-14 — global search trigger (also Cmd/Ctrl-K) + in-app help. */}
        <div className="app-tools">
          <button
            ref={searchBtnRef}
            type="button"
            className="app-search-trigger"
            aria-label="Search clients, services and team"
            aria-haspopup="dialog"
            onClick={openSearch}
          >
            <span className="app-search-trigger__icon" aria-hidden>
              ⌕
            </span>
            <span className="app-search-trigger__text">Search</span>
            <kbd className="app-search-trigger__kbd" aria-hidden>
              ⌘K
            </kbd>
          </button>
          <button
            ref={helpBtnRef}
            type="button"
            className="app-help-trigger"
            aria-label="Help and how-tos"
            aria-haspopup="dialog"
            onClick={openHelp}
          >
            ?
          </button>
          {/* WS-14 — notifications inbox bell (with the salon-wide unread badge). */}
          <NotificationsBell variant="rail" onOpen={openNotif} buttonRef={notifBtnRef} />
        </div>

        <nav className="app-nav">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={cx('app-nav__item', isActive(n.to) && 'app-nav__item--active')}
            >
              <span>{n.label}</span>
              {n.badge && waiting > 0 ? <Badge>{waiting}</Badge> : null}
            </Link>
          ))}
        </nav>

        <div className="app-rail__foot">
          <SyncChip />
          <div className="app-takings">
            <span className="slm-stat__label">Today, salon-wide</span>
            <span className="app-takings__value">{moneyWhole(takings.total)}</span>
            <span className="app-takings__sub">
              {pluralize(takings.clients, 'client')} · {takings.fromQueue} from the queue
            </span>
          </div>

          <div style={{ position: 'relative' }}>
            <button className="app-user" onClick={() => setMenuOpen((v) => !v)}>
              <Avatar initials={initials(salon?.name ?? 'Salon')} tone="ink" />
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span className="app-user__name">{salon?.name ?? 'Salon'}</span>
                <span className="app-user__role">{user ? `${roleLabel(user.role)} view` : ''}</span>
              </span>
            </button>
            {menuOpen ? (
              <UserMenu
                memberships={memberships ?? []}
                activeSalonId={user?.salonId}
                onSwitch={async (id) => {
                  setMenuOpen(false);
                  await switchSalon(id);
                }}
                onLogout={() => void logout()}
                onClose={() => setMenuOpen(false)}
              />
            ) : null}
          </div>
        </div>
      </aside>

      {/* ── Mobile top bar (≤768px only) ── */}
      <header className="app-topbar">
        <Link to="/" aria-label="Salonomy — home" className="app-logo app-logo--topbar">
          <Mark src={`${import.meta.env.BASE_URL}salonomy-logo.svg`} height={28} />
        </Link>
        <div className="app-topbar__actions">
          {/* WS-14 — search + help reachable on mobile too, beside the nav hamburger. */}
          <button
            type="button"
            className="app-topbar__icon"
            aria-label="Search clients, services and team"
            aria-haspopup="dialog"
            onClick={openSearch}
          >
            <span aria-hidden>⌕</span>
          </button>
          <button
            type="button"
            className="app-topbar__icon"
            aria-label="Help and how-tos"
            aria-haspopup="dialog"
            onClick={openHelp}
          >
            <span aria-hidden>?</span>
          </button>
          {/* WS-14 — notifications inbox bell, beside Search/Help on mobile too. */}
          <NotificationsBell variant="topbar" onOpen={openNotif} buttonRef={notifTopbarBtnRef} />
          <button
            ref={hamburgerRef}
            className="app-hamburger"
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="app-drawer"
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <span className="app-hamburger__bar" />
            <span className="app-hamburger__bar" />
            <span className="app-hamburger__bar" />
          </button>
        </div>
      </header>

      {/* ── Off-canvas drawer (≤768px) — rendered ONLY when open, so its controls are
          never in the tab order / AT tree while closed (no aria-modal trap). ── */}
      {drawerOpen ? (
        <>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="app-drawer-backdrop" aria-hidden onClick={closeDrawer} />
          <div
            id="app-drawer"
            ref={drawerRef}
            className="app-drawer app-drawer--open"
            role="dialog"
            aria-label="Navigation menu"
            aria-modal="true"
          >
            <nav className="app-nav">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className={cx('app-nav__item', isActive(n.to) && 'app-nav__item--active')}
                >
                  <span>{n.label}</span>
                  {n.badge && waiting > 0 ? <Badge>{waiting}</Badge> : null}
                </Link>
              ))}
            </nav>
            <div className="app-drawer__foot">
              <SyncChip />
              <button
                className="app-user"
                onClick={() => {
                  closeDrawer();
                  void logout();
                }}
              >
                <Avatar initials={initials(salon?.name ?? 'Salon')} tone="ink" />
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="app-user__name">{salon?.name ?? 'Salon'}</span>
                  <span className="app-user__role">
                    {user ? `${roleLabel(user.role)} view` : ''}
                  </span>
                </span>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void logout()}
                style={{ justifyContent: 'flex-start' }}
              >
                Sign out
              </Button>
            </div>
          </div>
        </>
      ) : null}

      <main className="app-main">{children}</main>

      {/* WS-14 — global search palette + in-app help. Self-render nothing while closed (no trap, no
          tab order); each is a proper aria-modal dialog with the shared focus trap. */}
      <CommandPalette open={searchOpen} onClose={closeSearch} />
      <HelpDrawer open={helpOpen} onClose={closeHelp} />
      <NotificationsDrawer open={notifOpen} onClose={closeNotif} />
    </div>
  );
}

function roleLabel(role: string): string {
  return (
    {
      owner: 'Owner',
      manager: 'Manager',
      stylist: 'Stylist',
      chair_renter: 'Renter',
      receptionist: 'Reception',
    }[role] ?? role
  );
}

function UserMenu({
  memberships,
  activeSalonId,
  onSwitch,
  onLogout,
  onClose,
}: {
  memberships: Array<{ salonId: string; salonName: string; role: string }>;
  activeSalonId: string | undefined;
  onSwitch: (id: string) => void;
  onLogout: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={onClose} aria-hidden />
      <div
        style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          zIndex: 41,
          background: '#fff',
          border: '1px solid var(--slm-border)',
          borderRadius: 14,
          boxShadow: 'var(--slm-shadow-pop)',
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {memberships.length > 1 ? (
          <>
            <div className="slm-stat__label" style={{ padding: '6px 8px' }}>
              Switch salon
            </div>
            {memberships.map((m) => (
              <button
                key={m.salonId}
                className="app-nav__item"
                style={{ fontSize: 13.5 }}
                onClick={() => onSwitch(m.salonId)}
              >
                <span>{m.salonName}</span>
                {m.salonId === activeSalonId ? <span className="muted">●</span> : null}
              </button>
            ))}
            <div style={{ height: 1, background: 'var(--slm-border)', margin: '6px 0' }} />
          </>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogout}
          style={{ justifyContent: 'flex-start' }}
        >
          Sign out
        </Button>
      </div>
    </>
  );
}
